// Compare Manager - Optimized version with request deduplication
(function() {
  'use strict';

  const COMPARE_STORAGE_KEY = 'garmin_compare_products';
  const MAX_COMPARE_PRODUCTS = 4;
  const SPEC_CACHE_KEY = 'garmin_spec_cache';
  const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  const MAX_CACHE_ITEMS = 2000; // Limit cache to 2000 products

  window.COMPARE_DEBUG = 1;
  
  // Debug mode (set window.COMPARE_DEBUG = true to enable logging)
  const debug = (...args) => {
    if (window.COMPARE_DEBUG) {
      console.log('[CompareManager]', ...args);
    }
  };

  // Compare Manager Object
  const CompareManager = {
    initializedHandles: new Set(), // Track which handles we've checked (not button elements)
    specCache: null, // { handle: { hasSpec: boolean, lastAccess: timestamp } }
    pendingRequests: new Map(), // Track in-flight requests to prevent duplicates

    // Initialize spec cache
    initCache: function() {
      try {
        const cached = localStorage.getItem(SPEC_CACHE_KEY);
        if (cached) {
          const data = JSON.parse(cached);
          
          if (data && data.cache && typeof data.cache === 'object' && data.timestamp) {
            if (Date.now() - data.timestamp < CACHE_DURATION) {
              // Migrate old cache format to new format if needed
              this.specCache = {};
              for (const [handle, value] of Object.entries(data.cache)) {
                if (typeof value === 'boolean') {
                  // Old format: convert to new format
                  this.specCache[handle] = {
                    hasSpec: value,
                    lastAccess: Date.now()
                  };
                } else if (value && typeof value === 'object' && typeof value.hasSpec === 'boolean') {
                  // New format: use as-is
                  this.specCache[handle] = value;
                }
              }
              debug('Cache loaded:', Object.keys(this.specCache).length, 'products');
            } else {
              debug('Cache expired, clearing');
              localStorage.removeItem(SPEC_CACHE_KEY);
            }
          }
        }
        
        if (!this.specCache) {
          this.specCache = {};
          debug('Initialized empty cache');
        }
      } catch (e) {
        console.error('Error initializing cache:', e);
        this.specCache = {};
      }
    },

    // Save cache
    saveCache: function() {
      try {
        localStorage.setItem(SPEC_CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          cache: this.specCache
        }));
      } catch (e) {
        console.error('Error saving cache:', e);
      }
    },

    // Evict oldest cache items when cache is full (LRU strategy)
    evictOldestCacheItems: function(count = 200) {
      const entries = Object.entries(this.specCache);
      
      // Sort by lastAccess (oldest first)
      entries.sort((a, b) => a[1].lastAccess - b[1].lastAccess);
      
      // Remove oldest entries
      for (let i = 0; i < Math.min(count, entries.length); i++) {
        delete this.specCache[entries[i][0]];
        this.initializedHandles.delete(entries[i][0]);
      }
      
      debug(`Evicted ${count} oldest cache items. Cache size: ${Object.keys(this.specCache).length}`);
    },

    // Clear cache
    clearCache: function() {
      this.specCache = {};
      localStorage.removeItem(SPEC_CACHE_KEY);
    },

    // Get products from localStorage
    getCompareProducts: function() {
      try {
        const stored = localStorage.getItem(COMPARE_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error reading compare products:', e);
        return [];
      }
    },

    // Save products to localStorage
    saveCompareProducts: function(products) {
      try {
        localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(products));
        this.updateAllButtonStates();
        this.updateFloatingBar(products);
      } catch (e) {
        console.error('Error saving compare products:', e);
      }
    },

    // Add product to comparison
    addProduct: function(productData) {
      let products = this.getCompareProducts();
      
      const exists = products.find(p => p.handle === productData.handle);
      if (exists) {
        this.showNotification('Produkt już jest w porównaniu', 'info');
        return false;
      }

      if (products.length >= MAX_COMPARE_PRODUCTS) {
        this.showNotification(`Możesz porównać maksymalnie ${MAX_COMPARE_PRODUCTS} produkty`, 'warning');
        return false;
      }

      products.push(productData);
      this.saveCompareProducts(products);
      this.showNotification('Dodano do porównania', 'success');
      return true;
    },

    // Remove product from comparison
    removeProduct: function(handle) {
      let products = this.getCompareProducts();
      products = products.filter(p => p.handle !== handle);
      this.saveCompareProducts(products);
      this.showNotification('Usunięto z porównania', 'info');
    },

    // Clear all products
    clearAll: function() {
      this.saveCompareProducts([]);
      this.showNotification('Wyczyszczono porównanie', 'info');
    },

    // Check if product has technical specification (with cache and deduplication)
    hasSpecification: async function(handle) {
      // Check cache first
      const cached = this.specCache[handle];
      if (cached && typeof cached.hasSpec === 'boolean') {
        // Update last access time
        cached.lastAccess = Date.now();
        debug(`Cache HIT for ${handle}:`, cached.hasSpec);
        return cached.hasSpec;
      }

      debug(`Cache MISS for ${handle}, fetching...`);

      // Check if there's already a pending request for this handle
      if (this.pendingRequests.has(handle)) {
        debug(`Waiting for pending request: ${handle}`);
        return this.pendingRequests.get(handle);
      }

      // Check if we need to evict old items before adding new ones
      const cacheSize = Object.keys(this.specCache).length;
      if (cacheSize >= MAX_CACHE_ITEMS) {
        debug(`Cache full (${cacheSize} items), evicting oldest 200 items`);
        this.evictOldestCacheItems(200);
      }

      // Create new request promise
      const requestPromise = this._fetchSpecification(handle);
      
      // Store the promise so other calls can wait for it
      this.pendingRequests.set(handle, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the pending request
        this.pendingRequests.delete(handle);
      }
    },

    // Internal method to actually fetch specification
    _fetchSpecification: async function(handle) {
      try {
        // Use the existing fetchTechnicalSpecification function which already handles the API call
        const data = await window.fetchTechnicalSpecification(handle);
        
        // Check if data is valid
        if (!data) {
          this.specCache[handle] = { hasSpec: false, lastAccess: Date.now() };
          this.saveCache();
          return false;
        }


        
        // Check if specification_table exists and is not null/empty
        if (!data.specification_table || 
            data.specification_table === null || 
            data.specification_table === '' ||
            data.specification_table === 'null') {
          this.specCache[handle] = { hasSpec: false, lastAccess: Date.now() };
          this.saveCache();
          return false;
        }
        
        // Try to parse if it's a string
        let spec = data.specification_table;

        if (typeof spec === 'string') {
          try {
            spec = JSON.parse(spec);
          } catch (e) {
            console.error(`Failed to parse specification_table for ${handle}:`, e);
            this.specCache[handle] = { hasSpec: false, lastAccess: Date.now() };
            this.saveCache();
            return false;
          }
        }
        
        // Check if spec is a valid object with sections
        if (!spec || typeof spec !== 'object') {
          this.specCache[handle] = { hasSpec: false, lastAccess: Date.now() };
          this.saveCache();
          return false;
        }
        
        // Check if any of the main sections exist and have data
        const sections = ['technical_specifications', 'functions', 'activity'];
        let hasData = false;
        
        for (const sectionName of sections) {
          if ((spec[sectionName] && Array.isArray(spec[sectionName]) && spec[sectionName].length > 0)) {
            // Check if at least one section has rows with data
            for (const section of spec[sectionName]) {
              if (section.rows && Array.isArray(section.rows) && section.rows.length > 0) {
                hasData = true;
                break;
              }
            }
          } else if (spec[sectionName].data.length > 0) {
            console.log(spec[sectionName].data[0].rows);
              if (spec[sectionName].data[0].rows && Array.isArray(spec[sectionName].data[0].rows) && spec[sectionName].data[0].rows.length > 0) {
                hasData = true;
                break;
              }
          }
          if (hasData) break;
        }
        
        this.specCache[handle] = { hasSpec: hasData, lastAccess: Date.now() };
        this.saveCache();
        return hasData;
        
      } catch (e) {
        console.error(`Error checking specification for ${handle}:`, e);
        this.specCache[handle] = { hasSpec: false, lastAccess: Date.now() };
        this.saveCache();
        return false;
      }
    },

    // Show notification
    showNotification: function(message, type = 'info') {
      const existing = document.querySelector('.compare-notification');
      if (existing) existing.remove();

      const notification = document.createElement('div');
      notification.className = `compare-notification compare-notification--${type}`;
      notification.textContent = message;
      document.body.appendChild(notification);

      setTimeout(() => notification.classList.add('show'), 10);

      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    },

    // Update floating comparison bar
    updateFloatingBar: function(products) {
      let bar = document.querySelector('.compare-floating-bar');
      
      if (products.length === 0) {
        if (bar) bar.remove();
        return;
      }

      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'compare-floating-bar';
        document.body.appendChild(bar);
      }

      const canCompare = products.length >= 2;
      
      bar.innerHTML = `
        <div class="compare-bar-content">
          <div class="compare-bar-products">
            ${products.map(product => `
              <div class="compare-bar-product">
                <img src="${product.image}" alt="${product.title}" class="compare-bar-image">
                <span class="compare-bar-title">${product.title}</span>
                <button class="compare-bar-remove" data-handle="${product.handle}" aria-label="Usuń">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            `).join('')}
          </div>
          <div class="compare-bar-actions">
            <button class="compare-bar-clear">
              Wyczyść
            </button>
            <button class="compare-bar-compare" ${!canCompare ? 'disabled' : ''}>
              ${canCompare ? 'Porównaj' : 'Min. 2'} (${products.length}/4)
            </button>
          </div>
        </div>
      `;

      // Add event listeners with event delegation
      bar.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.compare-bar-remove');
        if (removeBtn) {
          const handle = removeBtn.dataset.handle;
          this.removeProduct(handle);
          return;
        }

        if (e.target.classList.contains('compare-bar-clear') || 
            e.target.closest('.compare-bar-clear')) {
          if (confirm('Czy na pewno chcesz wyczyścić porównanie?')) {
            this.clearAll();
          }
          return;
        }

        if (e.target.classList.contains('compare-bar-compare') || 
            e.target.closest('.compare-bar-compare')) {
          if (products.length >= 2) {
            // Build URL with all product handles
            const handles = products.map((p, i) => `handle${i + 1}=${p.handle}`).join('&');
            window.location.href = `/pages/porownaj-produkty?${handles}`;
          }
          return;
        }
      });

      setTimeout(() => bar.classList.add('show'), 10);
    },

    // Update single button state - WITH "Porównaj" TEXT
    updateButtonState: function(button, products) {
      const handle = button.dataset.productHandle;
      const isAdded = products.find(p => p.handle === handle);
      
      if (isAdded) {
        button.classList.add('added');
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M13.5 3.5l-7 7-3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          <span class="compare-text">Dodano</span>
        `;
      } else {
        button.classList.remove('added');
        button.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20" fill="currentColor">
            <path d="M11.998 6.577l3.13.94c.115.03.222.101.293.215l3.5 5.5a.5.5 0 0 1 .074.339 4 4 0 0 1-7.92 0 .5.5 0 0 1 .071-.335L14.23 8.29l-2.616-.785a1.5 1.5 0 0 1-2.596-.779l-2.883-.865 2.753 4.371a.5.5 0 0 1 .072.338 4 4 0 0 1-7.919 0 .5.5 0 0 1 .072-.338l3.465-5.5a.5.5 0 0 1 .585-.206L9.206 5.74a1.5 1.5 0 0 1 2.792.837zM7.558 10L5 5.938 2.44 10h5.117zm.271 1H2.17a3.003 3.003 0 0 0 5.66 0zm9.76 2l-2.585-4.062L12.472 13h5.117zm.276 1h-5.66a3.003 3.003 0 0 0 5.66 0zM10.5 7a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
          </svg>
          <span class="compare-text">Porównaj</span>
        `;
      }

      if (products.length >= MAX_COMPARE_PRODUCTS && !isAdded) {
        button.disabled = true;
        button.title = `Możesz porównać maksymalnie ${MAX_COMPARE_PRODUCTS} produkty`;
      } else {
        button.disabled = false;
        button.title = isAdded ? 'Usuń z porównania' : 'Dodaj do porównania';
      }
    },

    // Update all button states
    updateAllButtonStates: function() {
      const products = this.getCompareProducts();
      const buttons = document.querySelectorAll('.add-to-compare-btn');
      
      buttons.forEach(btn => {
        const handle = btn.dataset.productHandle;
        if (this.initializedHandles.has(handle)) {
          this.updateButtonState(btn, products);
        }
      });
    },

    // Initialize compare button for a product
    initCompareButton: async function(button) {
      const handle = button.dataset.productHandle;
      const title = button.dataset.productTitle;
      const image = button.dataset.productImage;

      if (!handle || !title) {
        console.error('Missing product data on button:', button);
        button.style.display = 'none';
        return;
      }

      // If we've already checked this handle and have cached result, use it immediately
      const cached = this.specCache[handle];
      if (this.initializedHandles.has(handle) && cached && typeof cached.hasSpec === 'boolean') {
        debug(`Using existing cache for ${handle}:`, cached.hasSpec);
        
        // Update last access time
        cached.lastAccess = Date.now();
        
        if (!cached.hasSpec) {
          button.style.display = 'none';
          return;
        }

        // Product has specs - setup button
        button.disabled = false;
        button.style.display = '';

        // Add click handler
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          const products = this.getCompareProducts();
          const isAdded = products.find(p => p.handle === handle);

          if (isAdded) {
            this.removeProduct(handle);
          } else {
            this.addProduct({
              handle: handle,
              title: title,
              image: image
            });
          }
        };

        button.addEventListener('click', clickHandler);
        this.updateButtonState(button, this.getCompareProducts());
        return;
      }

      // Show loading state while checking
      button.disabled = true;
      button.innerHTML = '<span>Ładowanie...</span>';
      button.style.display = '';
      
      try {
        const hasSpec = await this.hasSpecification(handle);
        
        // Mark this handle as checked
        this.initializedHandles.add(handle);
        
        if (!hasSpec) {
          button.style.display = 'none';
          return;
        }

        // Product has specs - enable button
        button.disabled = false;
        button.style.display = '';

        // Add click handler
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();

          const products = this.getCompareProducts();
          const isAdded = products.find(p => p.handle === handle);

          if (isAdded) {
            this.removeProduct(handle);
          } else {
            this.addProduct({
              handle: handle,
              title: title,
              image: image
            });
          }
        };

        button.addEventListener('click', clickHandler);
        this.updateButtonState(button, this.getCompareProducts());
        
      } catch (error) {
        console.error(`Error initializing compare button for ${handle}:`, error);
        button.style.display = 'none';
        this.initializedHandles.add(handle);
      }
    },

    // Batch initialize buttons by unique handles
    initAllButtons: function() {
      const buttons = document.querySelectorAll('.add-to-compare-btn:not([data-compare-init])');
      
      if (buttons.length === 0) return;
      
      // Group buttons by handle to process each product only once
      const buttonsByHandle = new Map();
      
      buttons.forEach(btn => {
        btn.setAttribute('data-compare-init', 'true');
        const handle = btn.dataset.productHandle;
        
        if (!buttonsByHandle.has(handle)) {
          buttonsByHandle.set(handle, []);
        }
        buttonsByHandle.get(handle).push(btn);
      });

      debug(`Initializing ${buttons.length} buttons for ${buttonsByHandle.size} unique products`);

      // Process each unique product handle
      buttonsByHandle.forEach((buttonsForHandle, handle) => {
        // If we already have cached data for this handle, initialize all buttons immediately
        const cached = this.specCache[handle];
        if (this.initializedHandles.has(handle) && cached && typeof cached.hasSpec === 'boolean') {
          debug(`Already cached ${handle}, initializing ${buttonsForHandle.length} buttons instantly`);
          buttonsForHandle.forEach(btn => this.initCompareButton(btn));
          return;
        }

        // Otherwise, initialize the first button (this will fetch and cache)
        this.initCompareButton(buttonsForHandle[0]).then(() => {
          // After first button is initialized, update the rest using cached data
          for (let i = 1; i < buttonsForHandle.length; i++) {
            this.initCompareButton(buttonsForHandle[i]);
          }
        });
      });
    },

    // Initialize the manager
    init: function() {
      // Initialize cache
      this.initCache();
      
      // Update UI on page load
      const products = this.getCompareProducts();
      this.updateFloatingBar(products);

      // Initialize buttons
      this.initAllButtons();

      // Listen for dynamically added buttons (throttled)
      let initTimeout;
      const observer = new MutationObserver(() => {
        clearTimeout(initTimeout);
        initTimeout = setTimeout(() => {
          this.initAllButtons();
        }, 500);
      });

      // Only observe specific containers, not entire body
      const containers = document.querySelectorAll('.product-grid, .product-list, .collection-products, .mv-cfg-grid, .pc-grid');
      if (containers.length > 0) {
        containers.forEach(container => {
          observer.observe(container, {
            childList: true,
            subtree: true
          });
        });
      }
    }
  };

  // Export to window
  window.CompareManager = CompareManager;

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CompareManager.init());
  } else {
    CompareManager.init();
  }

})();
